import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  Filter,
  Loader2,
  Mail,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  ShieldOff,
  SlidersHorizontal,
  Trash2,
  UserCog,
  X,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SecretCodeModal from "../components/SecretCodeModal";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const PERMISSIONS = [
  { key: "manageClients", fr: "Clients", en: "Clients" },
  { key: "manageParticipants", fr: "Participants", en: "Participants" },
  { key: "manageCertificates", fr: "Certificats", en: "Certificates" },
  {
    key: "manageOrganizationUsers",
    fr: "Demandes et utilisateurs",
    en: "Requests and users",
  },
];

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "?";

const readSession = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return {
      token: localStorage.getItem("token"),
      isSuperAdmin:
        Boolean(user?.isSuperAdmin) || user?.adminRole === "SuperAdmin",
    };
  } catch {
    return { token: null, isSuperAdmin: false };
  }
};

const PermissionChip = ({ active, label, disabled, onClick, busy }) => (
  <button
    type="button"
    disabled={disabled || busy}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
      active
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
    }`}
  >
    {busy ? (
      <Loader2 className="h-3 w-3 animate-spin" />
    ) : active ? (
      <Check className="h-3 w-3" />
    ) : null}
    {label}
  </button>
);

const Admins = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [statusFilter, setStatusFilter] = useState("all");
  const [organizationFilter, setOrganizationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState("");
  const [updatingPermission, setUpdatingPermission] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletionCode, setDeletionCode] = useState("");
  const [deletingAdmin, setDeletingAdmin] = useState(false);

  const tr = (key, fr, en) =>
    t?.ui?.admins?.[key] || (lang === "en" ? en : fr);

  const fetchAdmins = async () => {
    const { token, isSuperAdmin } = readSession();
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    if (!isSuperAdmin) {
      navigate("/unauthorized", { replace: true });
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.message ||
            tr(
              "loadError",
              "Impossible de charger les administrateurs.",
              "Unable to load administrators."
            )
        );
      }
      setAdmins(Array.isArray(data) ? data : []);
    } catch (fetchError) {
      console.error("Admins fetch error:", fetchError);
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const organizations = useMemo(() => {
    const unique = new Map();
    admins.forEach((admin) => {
      if (admin.organizationRef?._id) {
        unique.set(admin.organizationRef._id, admin.organizationRef.name);
      }
    });
    return [...unique.entries()].sort((a, b) =>
      String(a[1]).localeCompare(String(b[1]), lang === "en" ? "en" : "fr")
    );
  }, [admins, lang]);

  const filteredAdmins = useMemo(() => {
    const query = deferredSearch.trim().toLocaleLowerCase(
      lang === "en" ? "en" : "fr"
    );

    const result = admins.filter((admin) => {
      const searchable = [
        admin.name,
        admin.email,
        admin.role,
        admin.organizationRef?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase(lang === "en" ? "en" : "fr");

      return (
        (!query || searchable.includes(query)) &&
        (statusFilter === "all" || admin.status === statusFilter) &&
        (organizationFilter === "all" ||
          String(admin.organizationRef?._id || "platform") === organizationFilter)
      );
    });

    return [...result].sort((left, right) => {
      if (sortBy === "name") {
        return String(left.name || "").localeCompare(
          String(right.name || ""),
          lang === "en" ? "en" : "fr"
        );
      }
      if (sortBy === "organization") {
        return String(left.organizationRef?.name || "").localeCompare(
          String(right.organizationRef?.name || ""),
          lang === "en" ? "en" : "fr"
        );
      }
      if (sortBy === "status") {
        return String(left.status).localeCompare(String(right.status));
      }
      return new Date(right.createdAt) - new Date(left.createdAt);
    });
  }, [admins, deferredSearch, statusFilter, organizationFilter, sortBy, lang]);

  const activeCount = admins.filter(
    (admin) => admin.role === "OrganizationAdmin" && admin.status === "Active"
  ).length;
  const organizationAdminCount = admins.filter(
    (admin) => admin.role === "OrganizationAdmin"
  ).length;

  const patchAdmin = (updatedAdmin) => {
    setAdmins((current) =>
      current.map((admin) =>
        admin._id === updatedAdmin._id ? updatedAdmin : admin
      )
    );
  };

  const toggleStatus = async (admin) => {
    if (admin.role === "SuperAdmin") return;
    const nextStatus = admin.status === "Active" ? "Inactive" : "Active";
    const confirmed = window.confirm(
      tr(
        "confirmStatus",
        `${nextStatus === "Active" ? "Activer" : "Désactiver"} le compte de ${admin.name} ?`,
        `${nextStatus === "Active" ? "Activate" : "Deactivate"} ${admin.name}'s account?`
      )
    );
    if (!confirmed) return;

    setUpdatingStatusId(admin._id);
    setError("");
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admins/${admin._id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Update failed");
      patchAdmin(data.admin);
      setMessage(
        tr(
          "statusUpdated",
          "Le statut de l’administrateur a été mis à jour.",
          "Administrator status updated."
        )
      );
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setUpdatingStatusId("");
    }
  };

  const togglePermission = async (admin, permissionKey) => {
    if (admin.role === "SuperAdmin") return;
    const operationKey = `${admin._id}:${permissionKey}`;
    setUpdatingPermission(operationKey);
    setError("");
    setMessage("");

    try {
      const nextPermissions = {
        ...admin.permissions,
        [permissionKey]: !Boolean(admin.permissions?.[permissionKey]),
      };
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admins/${admin._id}/permissions`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ permissions: nextPermissions }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Update failed");
      patchAdmin(data.admin);
      setMessage(
        tr(
          "permissionsUpdated",
          "Les accès de l’administrateur ont été mis à jour.",
          "Administrator access updated."
        )
      );
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setUpdatingPermission("");
    }
  };



  const openDeleteAdmin = (admin) => {
    if (admin.role === "SuperAdmin") return;
    setDeleteTarget(admin);
    setDeletionCode("");
    setError("");
    setMessage("");
  };

  const confirmDeleteAdmin = async () => {
    if (!deleteTarget || !deletionCode.trim()) return;
    setDeletingAdmin(true);
    setError("");
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admins/${deleteTarget._id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ secretCode: deletionCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.message ||
            tr(
              "deleteError",
              "Impossible de supprimer cet administrateur.",
              "Unable to remove this administrator."
            )
        );
      }

      setAdmins((current) =>
        current.filter((admin) => admin._id !== deleteTarget._id)
      );
      setMessage(
        tr(
          "deleteSuccess",
          "L’administrateur a été supprimé. Son organisation et ses clients ont été désactivés sans supprimer leurs données.",
          "The administrator was removed. The organization and its clients were deactivated without deleting their data."
        )
      );
      setDeleteTarget(null);
      setDeletionCode("");
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeletingAdmin(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setOrganizationFilter("all");
    setSortBy("newest");
  };

  const activeFilters =
    Number(statusFilter !== "all") + Number(organizationFilter !== "all");

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-3 text-sm font-semibold text-slate-600">
            {tr(
              "loading",
              "Chargement des administrateurs...",
              "Loading administrators..."
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-3 pb-10 pt-20 sm:px-5">
      <div className="mx-auto max-w-[1500px]">
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-6 text-white shadow-xl sm:px-7">
          <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-violet-500/20 blur-2xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <UserCog className="h-6 w-6 text-cyan-300" />
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                  {tr("platformAccess", "Accès plateforme", "Platform access")}
                </p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                  {tr(
                    "title",
                    "Gestion des administrateurs",
                    "Administrator management"
                  )}
                </h1>
                <p className="mt-1 text-sm text-slate-300">
                  {tr(
                    "subtitle",
                    "Activez les comptes et définissez précisément leurs accès dans leur organisation.",
                    "Activate accounts and define their organization access precisely."
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <div className="text-2xl font-bold">{organizationAdminCount}</div>
                <div className="text-xs uppercase tracking-wider text-slate-300">
                  {tr("organizationAdmins", "Admins d’organisation", "Organization admins")}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <div className="text-2xl font-bold text-emerald-300">{activeCount}</div>
                <div className="text-xs uppercase tracking-wider text-slate-300">
                  {tr("active", "Actifs", "Active")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {message}
          </div>
        ) : null}

        <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={tr(
                  "searchPlaceholder",
                  "Rechercher par nom, email ou organisation...",
                  "Search by name, email or organization..."
                )}
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-10 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none"
            >
              <option value="newest">{tr("newest", "Plus récents", "Newest")}</option>
              <option value="name">{tr("nameSort", "Nom A–Z", "Name A–Z")}</option>
              <option value="organization">
                {tr("organizationSort", "Organisation A–Z", "Organization A–Z")}
              </option>
              <option value="status">{tr("statusSort", "Statut", "Status")}</option>
            </select>

            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold transition ${
                showFilters || activeFilters
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <Filter className="h-4 w-4" />
              {tr("filters", "Filtres", "Filters")}
              {activeFilters ? (
                <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] text-white">
                  {activeFilters}
                </span>
              ) : null}
            </button>

            <button
              type="button"
              onClick={fetchAdmins}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              title={tr("refresh", "Actualiser", "Refresh")}
            >
              <RefreshCw className="h-4 w-4" />
            </button>

            {(activeFilters || searchTerm) && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-rose-50 hover:text-rose-600"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            )}
          </div>

          {showFilters ? (
            <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2">
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none"
              >
                <option value="all">{tr("allStatuses", "Tous les statuts", "All statuses")}</option>
                <option value="Active">{tr("active", "Actifs", "Active")}</option>
                <option value="Inactive">{tr("inactive", "Inactifs", "Inactive")}</option>
              </select>
              <select
                value={organizationFilter}
                onChange={(event) => setOrganizationFilter(event.target.value)}
                className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none"
              >
                <option value="all">{tr("allOrganizations", "Toutes les organisations", "All organizations")}</option>
                <option value="platform">CertiLink Platform</option>
                {organizations.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </section>

        <section className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
          <div className="hidden max-h-[70vh] overflow-auto lg:block">
            <table className="w-full min-w-[1120px] table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[20%]" />
                <col className="w-[19%]" />
                <col className="w-[13%]" />
                <col className="w-[35%]" />
                <col className="w-[13%]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase tracking-wider text-slate-300">
                <tr>
                  <th className="px-4 py-3">{tr("administrator", "Administrateur", "Administrator")}</th>
                  <th className="px-4 py-3">{tr("organization", "Organisation", "Organization")}</th>
                  <th className="px-4 py-3 text-center">{tr("status", "Statut", "Status")}</th>
                  <th className="px-4 py-3">{tr("access", "Accès", "Access")}</th>
                  <th className="px-4 py-3 text-center">{tr("action", "Action", "Action")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAdmins.map((admin) => {
                  const protectedAccount = admin.role === "SuperAdmin";
                  return (
                    <tr key={admin._id} className="align-top transition hover:bg-blue-50/50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-black text-white ${protectedAccount ? "bg-gradient-to-br from-violet-600 to-indigo-600" : "bg-gradient-to-br from-blue-600 to-cyan-500"}`}>
                            {getInitials(admin.name)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-bold text-slate-900">{admin.name}</p>
                            <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-500">
                              <Mail className="h-3 w-3" /> {admin.email}
                            </p>
                            <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-blue-600">
                              {protectedAccount
                                ? tr("superAdmin", "Super Admin", "Super Admin")
                                : tr("organizationAdmin", "Admin d’organisation", "Organization admin")}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="font-semibold">
                              {admin.organizationRef?.name || "CertiLink Platform"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${admin.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${admin.status === "Active" ? "bg-emerald-500" : "bg-rose-500"}`} />
                          {admin.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {protectedAccount ? (
                          <div className="inline-flex items-center gap-2 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700">
                            <ShieldCheck className="h-4 w-4" />
                            {tr("fullAccess", "Accès complet protégé", "Protected full access")}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {PERMISSIONS.map((permission) => {
                              const operationKey = `${admin._id}:${permission.key}`;
                              return (
                                <PermissionChip
                                  key={permission.key}
                                  label={lang === "en" ? permission.en : permission.fr}
                                  active={Boolean(admin.permissions?.[permission.key])}
                                  busy={updatingPermission === operationKey}
                                  disabled={updatingPermission && updatingPermission !== operationKey}
                                  onClick={() => togglePermission(admin, permission.key)}
                                />
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {protectedAccount ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-400">
                            <ShieldCheck className="h-4 w-4" />
                            {tr("protected", "Protégé", "Protected")}
                          </span>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              disabled={updatingStatusId === admin._id}
                              onClick={() => toggleStatus(admin)}
                              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition ${admin.status === "Active" ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                            >
                              {updatingStatusId === admin._id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : admin.status === "Active" ? (
                                <ShieldOff className="h-4 w-4" />
                              ) : (
                                <ShieldCheck className="h-4 w-4" />
                              )}
                              {admin.status === "Active"
                                ? tr("deactivate", "Désactiver", "Deactivate")
                                : tr("activate", "Activer", "Activate")}
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteAdmin(admin)}
                              title={tr("delete", "Supprimer", "Delete")}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-100 lg:hidden">
            {filteredAdmins.map((admin) => {
              const protectedAccount = admin.role === "SuperAdmin";
              return (
                <article key={admin._id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-black text-white">
                        {getInitials(admin.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-slate-900">{admin.name}</p>
                        <p className="truncate text-xs text-slate-500">{admin.email}</p>
                        <p className="mt-1 text-xs font-semibold text-blue-700">
                          {admin.organizationRef?.name || "CertiLink Platform"}
                        </p>
                      </div>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${admin.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      {admin.status}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {protectedAccount ? (
                      <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700">
                        {tr("fullAccess", "Accès complet protégé", "Protected full access")}
                      </span>
                    ) : (
                      PERMISSIONS.map((permission) => (
                        <PermissionChip
                          key={permission.key}
                          label={lang === "en" ? permission.en : permission.fr}
                          active={Boolean(admin.permissions?.[permission.key])}
                          busy={updatingPermission === `${admin._id}:${permission.key}`}
                          disabled={Boolean(updatingPermission) && updatingPermission !== `${admin._id}:${permission.key}`}
                          onClick={() => togglePermission(admin, permission.key)}
                        />
                      ))
                    )}
                  </div>

                  {!protectedAccount ? (
                    <div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
                      <button
                        type="button"
                        onClick={() => toggleStatus(admin)}
                        className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-bold ${admin.status === "Active" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
                      >
                        {admin.status === "Active" ? (
                          <ShieldOff className="h-4 w-4" />
                        ) : (
                          <ShieldCheck className="h-4 w-4" />
                        )}
                        {admin.status === "Active"
                          ? tr("deactivate", "Désactiver", "Deactivate")
                          : tr("activate", "Activer", "Activate")}
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeleteAdmin(admin)}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700"
                        title={tr("delete", "Supprimer", "Delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>

          {!filteredAdmins.length ? (
            <div className="px-6 py-16 text-center">
              <SlidersHorizontal className="mx-auto h-10 w-10 text-slate-300" />
              <h2 className="mt-3 font-bold text-slate-900">
                {tr("noAdmin", "Aucun administrateur trouvé", "No administrator found")}
              </h2>
            </div>
          ) : null}
        </section>
      </div>

      <SecretCodeModal
        open={Boolean(deleteTarget)}
        title={tr("deleteTitle", "Supprimer cet administrateur", "Remove this administrator")}
        description={tr(
          "deleteDescription",
          `L’administrateur « ${deleteTarget?.name || ""} » sera retiré. Son organisation et tous ses clients seront désactivés. Les participants, certificats, thèmes et modèles seront conservés.`,
          `Administrator “${deleteTarget?.name || ""}” will be removed. The organization and all its clients will be deactivated. Participants, certificates, themes and templates will be preserved.`
        )}
        code={deletionCode}
        setCode={setDeletionCode}
        onClose={() => {
          if (!deletingAdmin) {
            setDeleteTarget(null);
            setDeletionCode("");
          }
        }}
        onConfirm={confirmDeleteAdmin}
        loading={deletingAdmin}
        labels={{
          protectedDeletion: tr("protectedDeletion", "Suppression protégée", "Protected deletion"),
          codeLabel: tr("superAdminDeletionCode", "Code de suppression du Super Admin", "Super Admin deletion code"),
          codePlaceholder: tr("codePlaceholder", "Saisissez votre code de suppression", "Enter your deletion code"),
          cancel: tr("cancel", "Annuler", "Cancel"),
          confirm: tr("confirmDelete", "Confirmer la suppression", "Confirm removal"),
          deleting: tr("deleting", "Suppression…", "Removing…"),
          close: tr("close", "Fermer", "Close"),
        }}
      />
    </main>
  );
};

export default Admins;
